import { debugLog } from '@ui-common/utils'
import { useCallback, useEffect, useReducer, useState } from 'react'
import {
  ArrayPath,
  Control,
  FieldArray,
  FieldArrayMethodProps,
  FieldArrayPath,
  FieldArrayWithId,
  FieldValues,
  Path,
  PathValue,
  useFieldArray,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  UseFormWatch,
} from 'react-hook-form'

interface UseDynamicFormFieldParams<TFormData extends FieldValues> {
  name: FieldArrayPath<TFormData>
  control: Control<TFormData>
  watch: UseFormWatch<TFormData>
  shouldUnregisterFields?: boolean
  initialLengthForFocusFix?: number
  optimize?: boolean
}

//TODO: DIG-3832 something weird is going on here, with FieldArrayWithId change + useForm.shouldUnregister=true, the item default id will never be in the submitted data, this could be beneficial since you wont have to provide id to default or appended value to the array but this needs further testing
export type RenderFieldParams<TFormData extends FieldValues> = FieldArrayWithId<TFormData, ArrayPath<TFormData>> &
  PathValue<TFormData, Path<TFormData>>

export interface UseDynamicFormFieldReturn<TFormData extends FieldValues = FieldValues> {
  renderFields: RenderFieldParams<TFormData>[]
  appendField: <T extends FieldArray<TFormData, ArrayPath<TFormData>>>(object: T, opts?: FieldArrayMethodProps) => void
  removeField: (event?: React.MouseEvent<Element>, index?: number) => void
  updateField: UseFieldArrayUpdate<TFormData, ArrayPath<TFormData>>
  replaceFields: UseFieldArrayReplace<TFormData, ArrayPath<TFormData>>
}

/**
 * Custom hook for generating form arrays that can be dynamically added or removed by the user
 *
 * @param name - name of the array in the form
 * @param control - control function you get from useWizardForm hook
 * @param watch - watch function you get from useWizardForm hook
 * @param shouldUnregisterFields - unregister field data automatically from formData when field is not shown (should be usually set true)
 * @param initialLengthForFocusFix - When appending a dynamic form field, and the first focused input is unsupported by React Hook Form (e.g. radio), pass this to enable the fix
 * @param optimize - optimize=true by not having controlled field array and avoid unnecessary re-rendering
 */
export const useDynamicFormField = <TFormData extends FieldValues>({
  name,
  control,
  watch,
  shouldUnregisterFields = false,
  initialLengthForFocusFix,
  optimize = false,
}: UseDynamicFormFieldParams<TFormData>): UseDynamicFormFieldReturn<TFormData> => {
  const { fields, remove, append, update, replace } = useFieldArray<TFormData>({
    control,
    name,
    shouldUnregister: shouldUnregisterFields,
  })

  const getControlledRenderFields = () => {
    const watchName = name as Path<TFormData>
    const watchFields = fields as PathValue<TFormData, Path<TFormData>>
    const fieldArray = watch(watchName, watchFields)

    return fields.map((field, index) => {
      return {
        ...field,
        ...fieldArray[index],
      }
    })
  }

  const renderFields = optimize ? fields : getControlledRenderFields()

  const [appendedIndex, updateAppendedIndex] = useReducer(
    (v: number, change: number) => v + change,
    (initialLengthForFocusFix ?? 0) - 1,
  )

  const [focusFieldIndex, setFocusFieldIndex] = useState<number | undefined>()
  const appendField = useCallback(
    <T extends FieldArray<TFormData, ArrayPath<TFormData>>>(object: T, appendCallOpts?: FieldArrayMethodProps) => {
      if (!appendCallOpts) {
        appendCallOpts = {}
      }
      const shouldNotFocus = appendCallOpts.shouldFocus === false

      if (initialLengthForFocusFix !== undefined) {
        if (appendCallOpts.focusIndex === undefined) {
          throw new Error(`focusIndex is required when using initialLengthForFocusFix`)
        }
        appendCallOpts.shouldFocus = false
      }
      append(object, appendCallOpts)

      if (initialLengthForFocusFix !== undefined) {
        setFocusFieldIndex(shouldNotFocus ? undefined : appendCallOpts.focusIndex)
        updateAppendedIndex(1)
      }
    },
    [append, initialLengthForFocusFix],
  )

  const removeField = useCallback(
    (event?: React.MouseEvent<Element>, index?: number) => {
      if (index !== undefined && index !== null) {
        if (event) {
          event.preventDefault()
        }
        if (!optimize) {
          for (let i = index + 1; i < renderFields.length; i++) {
            update(i, { ...renderFields[i], id: `${name}.${i - 1}` })
          }
        }
        remove(index)
      } else {
        remove()
      }

      if (initialLengthForFocusFix !== undefined) {
        setFocusFieldIndex(undefined)
        updateAppendedIndex(-1)
      }
    },
    [renderFields, initialLengthForFocusFix, name, remove, update, optimize],
  )

  useEffect(() => {
    if (
      initialLengthForFocusFix === undefined ||
      appendedIndex === initialLengthForFocusFix - 1 ||
      focusFieldIndex === undefined
    ) {
      return
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const refs = (Object.values((control?._fields?.[name] as any)[appendedIndex] || {})[focusFieldIndex] as any)._f
        .refs

      if (refs.length > 0) {
        refs[0].focus()
      }
    } catch (e) {
      debugLog(`React Hook Form underscored property usage failed: ${(e as Error).message}`)
    }
  }, [appendedIndex, control._fields, focusFieldIndex, initialLengthForFocusFix, name])

  return {
    renderFields,
    appendField,
    removeField,
    updateField: update,
    replaceFields: replace,
  }
}
